import { inject, Injectable, signal, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, Subscription, interval } from 'rxjs';
import { map, switchMap, filter } from 'rxjs/operators';
import { IUserProfile } from '../interfaces/user-profile.interface';
import { environment } from '../../../environments/environment';
import {
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { MSAL_SILENT_REDIRECT_URI } from '../../msal-config.factory';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private msalService = inject(MsalService);
  private msalBroadcast = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private tokenRefreshSubscription?: Subscription;
  private readonly USER_PROFILE_STORAGE_KEY = 'msal.user.profile';

  private userProfileSignal = signal<IUserProfile | null>(null);
  readonly userProfile = this.userProfileSignal.asReadonly();

  private loginSuccessSignal = signal<boolean>(false);
  readonly loginSuccess = this.loginSuccessSignal.asReadonly();

  constructor() {
    this.ensureActiveAccount();
    this.loadCachedUserProfile();
    this.msalBroadcast.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
      )
      .subscribe(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (
          accounts.length > 0 &&
          !this.msalService.instance.getActiveAccount()
        ) {
          this.msalService.instance.setActiveAccount(accounts[0]);
        }
        setTimeout(() => {
          this.loginSuccessSignal.set(true);
          setTimeout(() => this.loginSuccessSignal.set(false), 1000);
        }, 300);
        this.startTokenRefresh();
      });

    if (this.isAuthenticated()) {
      this.startTokenRefresh();
    }
  }

  private ensureActiveAccount(): void {
    if (this.msalService.instance.getActiveAccount()) {
      return;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngOnDestroy(): void {
    this.tokenRefreshSubscription?.unsubscribe();
  }

  private startTokenRefresh(): void {
    this.tokenRefreshSubscription?.unsubscribe();

    this.tokenRefreshSubscription = interval(1800000).subscribe(() => {
      this.refreshTokenSilently();
    });

    setTimeout(() => {
      this.refreshTokenSilently();
    }, 1000);
  }

  private async refreshTokenSilently(): Promise<void> {
    this.ensureActiveAccount();
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      return;
    }

    try {
      await this.msalService.acquireTokenSilent({
        scopes: [environment.mslConfig.apiScopes],
        account: activeAccount,
        forceRefresh: false,
        redirectUri: MSAL_SILENT_REDIRECT_URI,
      });
      console.log('Token successfully refreshed in background');
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.msalService.instance.loginRedirect({
          scopes: [
            'openid',
            'profile',
            'email',
            'offline_access',
            environment.mslConfig.apiScopes,
          ],
        });
        return;
      }
      console.error('Token refresh failed:', error);
    }
  }

  private getUserProfile(): Observable<IUserProfile> {
    return this.msalService
      .acquireTokenSilent({
        scopes: ['https://graph.microsoft.com/User.Read'],
        account: this.msalService.instance.getActiveAccount() ?? undefined,
        redirectUri: MSAL_SILENT_REDIRECT_URI,
      })
      .pipe(
        switchMap((result) => {
          return this.http.get<IUserProfile>(
            'https://graph.microsoft.com/v1.0/me',
            {
              headers: {
                Authorization: `Bearer ${result.accessToken}`,
              },
            },
          );
        }),
        map((profile) => {
          this.userProfileSignal.set(profile);
          this.saveCachedUserProfile(profile);
          return profile;
        }),
      );
  }

  private loadCachedUserProfile(): void {
    try {
      const cached = localStorage.getItem(this.USER_PROFILE_STORAGE_KEY);
      if (cached && this.isAuthenticated()) {
        const profile = JSON.parse(cached) as IUserProfile;
        this.userProfileSignal.set(profile);
        console.log('📦 User profile loaded from cache');
      }
    } catch (error) {
      console.warn('Failed to load cached user profile:', error);
      localStorage.removeItem(this.USER_PROFILE_STORAGE_KEY);
    }
  }

  private saveCachedUserProfile(profile: IUserProfile): void {
    try {
      localStorage.setItem(
        this.USER_PROFILE_STORAGE_KEY,
        JSON.stringify(profile),
      );
    } catch (error) {
      console.warn('Failed to save user profile to cache:', error);
    }
  }

  clearUserProfile(): void {
    this.userProfileSignal.set(null);
    localStorage.removeItem(this.USER_PROFILE_STORAGE_KEY);
  }

  isAuthenticated(): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return true;
    }

    const demoToken = sessionStorage.getItem('msal.demo.token');
    if (demoToken) {
      return true;
    }

    return false;
  }

  async getUserProfileAsync(): Promise<IUserProfile> {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      throw new Error('No active account. User is not authenticated.');
    }

    // Return cached profile if available
    const cachedProfile = this.userProfileSignal();
    if (cachedProfile) {
      console.log('📦 Using cached user profile');
      return cachedProfile;
    }

    // Fetch fresh profile from API
    return firstValueFrom(this.getUserProfile());
  }

  getUserDisplayName(): string {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount?.name) {
      return activeAccount.name;
    }

    const demoToken = sessionStorage.getItem('msal.demo.token');
    if (demoToken) {
      try {
        const tokenData = JSON.parse(demoToken) as Record<string, unknown>;
        const account = tokenData['account'] as Record<string, unknown>;
        if (account && typeof account['name'] === 'string') {
          return account['name'];
        }
      } catch {
        /* empty */
      }
    }

    return '';
  }

  getAccounts() {
    return this.msalService.instance.getAllAccounts();
  }

  getIdTokenClaims(): Record<string, unknown> | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    return (activeAccount?.idTokenClaims as Record<string, unknown>) ?? null;
  }

  async loginAsync(): Promise<void> {
    try {
      const inProgress = this.msalService.instance.getActiveAccount() !== null;
      if (inProgress) {
        console.warn('Interaction already in progress');
        return;
      }

      await this.msalService.instance.loginRedirect({
        scopes: [
          'openid',
          'profile',
          'email',
          'offline_access',
          environment.mslConfig.apiScopes,
        ],
      });
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('interaction_in_progress')
      ) {
        console.warn(
          'Login interaction already in progress, waiting for completion',
        );
        return;
      }
      console.error('Login failed:', error);
      throw error;
    }
  }

  private logout(): Observable<void> {
    return this.msalService.logoutRedirect();
  }

  async logoutAsync(): Promise<void> {
    this.clearUserProfile();

    const demoToken = sessionStorage.getItem('msal.demo.token');
    if (demoToken) {
      sessionStorage.removeItem('msal.demo.token');
      console.log('Demo logout successful');
      return;
    }

    return firstValueFrom(this.logout());
  }

  async demoLoginAsync(): Promise<void> {
    try {
      const DEMO_USERNAME = 'demo@myfruit.com';
      const DEMO_PASSWORD = 'DemoPass123!';

      const msalInstance = this.msalService.instance as unknown as {
        acquireTokenByUsername?: (
          config: Record<string, unknown>,
        ) => Promise<unknown>;
      };

      if (msalInstance.acquireTokenByUsername) {
        try {
          const response = await msalInstance.acquireTokenByUsername({
            username: DEMO_USERNAME,
            password: DEMO_PASSWORD,
            scopes: [
              'openid',
              'profile',
              'email',
              environment.mslConfig.apiScopes,
            ],
          });

          const tokenResponse = response as Record<string, unknown>;
          if (tokenResponse['account']) {
            this.msalService.instance.setActiveAccount(
              tokenResponse['account'] as never,
            );
            sessionStorage.setItem('msal.demo.token', JSON.stringify(response));

            setTimeout(() => {
              this.loginSuccessSignal.set(true);
              setTimeout(() => this.loginSuccessSignal.set(false), 1000);
            }, 300);

            const accountInfo = tokenResponse['account'] as Record<
              string,
              unknown
            >;
            console.log(
              'Demo login successful - Connected to Azure:',
              accountInfo['name'],
            );
            return;
          }
        } catch (ropcError) {
          console.log(
            'ROPC flow not available, using mock token...',
            ropcError,
          );
        }
      }

      await this.demoLoginMockAsync();
    } catch (error) {
      console.error('Demo login failed:', error);
      throw error;
    }
  }

  private async demoLoginMockAsync(): Promise<void> {
    try {
      const demoAccount = {
        name: 'Demo User',
        username: 'demo@myfruit.com',
        homeAccountId: 'demo-user-id.demo-tenant',
        localAccountId: 'demo-user-local-id',
        environment: 'demo',
        tenantId: 'demo-tenant-id',
        idTokenClaims: {
          oid: 'demo-user-oid',
          sub: 'demo-user-sub',
          email: 'demo@myfruit.com',
          name: 'Demo User',
          given_name: 'Demo',
          family_name: 'User',
        } as Record<string, unknown>,
      } as unknown;

      const instance = this.msalService.instance as unknown as {
        _account: unknown;
        _activeAccount: unknown;
      };
      instance._account = demoAccount;
      instance._activeAccount = demoAccount;

      const demoTokenResponse = {
        accessToken: 'demo-access-token-' + Date.now(),
        expiresOn: new Date(Date.now() + 3600000),
        idToken: 'demo-id-token-' + Date.now(),
        refreshToken: 'demo-refresh-token',
        account: demoAccount,
      };

      sessionStorage.setItem(
        'msal.demo.token',
        JSON.stringify(demoTokenResponse),
      );

      setTimeout(() => {
        this.loginSuccessSignal.set(true);
        setTimeout(() => this.loginSuccessSignal.set(false), 1000);
      }, 300);

      console.log('Mock demo login successful (fallback)');
    } catch (error) {
      console.error('Mock demo login failed:', error);
      throw error;
    }
  }
}
